import styled, { DefaultTheme } from 'styled-components';
import { Card, Button, Input, Space, Typography, Table, Switch, Progress, Radio, Tabs, Alert, Tag, Avatar } from 'antd';
import { CopyOutlined, CheckOutlined, GlobalOutlined, InfoCircleOutlined } from '@ant-design/icons';

declare module 'styled-components' {
  export interface DefaultTheme {
    mode?: 'light' | 'dark';
  }
}

const getThemeValue = <T,>(props: { theme?: DefaultTheme }, darkValue: T, lightValue: T): T => {
  return props.theme?.mode === 'dark' ? darkValue : lightValue;
};

export const ActionIconButton = styled.div<{ iconColor?: string; $loading?: boolean }>`
  align-items: center;
  border: 1px solid ${(props) => getThemeValue(props, '#434343', '#eee')};
  border-radius: 10px;
  display: flex;
  height: 30px;
  justify-content: center;
  padding: 8px;
  width: 30px;
  cursor: ${(props) => (props.$loading ? 'not-allowed' : 'pointer')};
  opacity: ${(props) => (props.$loading ? 0.5 : 1)};
  transition: background-color 0.2s, color 0.2s, opacity 0.2s;
  background: ${(props) => getThemeValue(props, 'transparent', 'inherit')};

  svg {
    transition: color 0.2s;
  }

  &:hover {
    background-color: ${(props) =>
      props.$loading
        ? 'transparent'
        : (() => {
            const iconColor = props.iconColor || '#2D6CCA';
            if (iconColor === '#f5222d') {
              return getThemeValue(props, 'rgba(245, 34, 45, 0.2)', 'rgba(245, 34, 45, 0.1)');
            }
            return getThemeValue(props, 'rgba(45, 108, 202, 0.2)', 'rgba(45, 108, 202, 0.1)');
          })()} !important;

    svg {
      color: ${(props) => (props.$loading ? 'inherit' : props.iconColor || '#2D6CCA')} !important;
    }
  }
`;

export const Container = styled.div`
  width: 100%;
  padding: 24px;
  background: ${(props) => getThemeValue(props, '#0d1117', '#f5f5f5')};

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

export const HeaderSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 16px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

export const Title = styled(Typography.Title)`
  margin: 0 !important;
  color: ${(props) => getThemeValue(props, '#fff', '#1a1a1a')} !important;
  font-weight: 700 !important;
`;

export const Subtitle = styled(Typography.Text)`
  font-size: 16px;
  color: ${(props) => getThemeValue(props, '#8b949e', '#666')} !important;
  display: block;
  margin-top: 8px;
`;

export const ProfileCard = styled(Card)`
  border-radius: 12px;
  border: 1px solid ${(props) => getThemeValue(props, '#30363d', '#e8e8e8')};
  background: ${(props) => getThemeValue(props, '#161b22', '#ffffff')};
  margin-bottom: 24px;
  box-shadow: ${(props) => getThemeValue(props, '0 2px 8px rgba(0, 0, 0, 0.3)', '0 2px 8px rgba(0, 0, 0, 0.08)')};

  .ant-card-body {
    padding: 16px;
  }
`;

export const ProfileHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 24px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
  }
`;

export const ProfileInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1;

  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
  }
`;

export const ProfileDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const ProfileName = styled(Typography.Title)`
  margin: 0 !important;
  color: ${(props) => getThemeValue(props, '#fff', '#1a1a1a')} !important;
  font-size: 24px !important;
`;

export const ProfileUsername = styled(Typography.Text)`
  font-size: 16px;
  color: ${(props) => getThemeValue(props, '#8b949e', '#666')} !important;
`;

export const ProfileLocation = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  color: ${(props) => getThemeValue(props, '#8b949e', '#666')};
  font-size: 14px;
`;

export const SettingsCard = styled(Card)`
  border-radius: 12px;
  border: 1px solid ${(props) => getThemeValue(props, '#30363d', '#e8e8e8')};
  background: ${(props) => getThemeValue(props, '#161b22', '#ffffff')};
  box-shadow: ${(props) => getThemeValue(props, '0 2px 8px rgba(0, 0, 0, 0.3)', '0 2px 8px rgba(0, 0, 0, 0.08)')};
  transition: all 0.3s ease;

  &:hover {
    box-shadow: ${(props) => getThemeValue(props, '0 4px 12px rgba(0, 0, 0, 0.4)', '0 4px 12px rgba(0, 0, 0, 0.12)')};
  }

  .ant-card-body {
    padding: 16px;
  }
`;

export const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

export const CardTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const CardTitleText = styled(Typography.Title)`
  margin: 0 !important;
  color: ${(props) => getThemeValue(props, '#fff', '#1a1a1a')} !important;
  font-size: 18px !important;
`;

export const InfoRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: ${(props) => getThemeValue(props, '#0d1117', '#f9f9f9')};
  border-radius: 8px;
  border: 1px solid ${(props) => getThemeValue(props, '#21262d', '#e8e8e8')};
  margin-bottom: 12px;
`;

export const InfoLabel = styled(Typography.Text)`
  font-size: 14px;
  color: ${(props) => getThemeValue(props, '#8b949e', '#666')} !important;
  font-weight: 500;
  min-width: 120px;
`;

export const InfoValue = styled(Typography.Text)`
  font-size: 14px;
  color: ${(props) => getThemeValue(props, '#fff', '#1a1a1a')} !important;
  font-weight: 600;
  flex: 1;
`;

export const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const StyledInput = styled(Input)`
  padding: 7px 10px !important;
  border-radius: 8px !important;
`;

export const StyledButton = styled(Button)`
  border-radius: 8px;
  height: 40px;
  font-weight: 500;
`;

export const SaveChangesButton = styled(Button)`
  background-color: #2D6CCA !important;
  color: #ffffff !important;
  font-size: 16px !important;
  border-radius: 8px !important;
  padding: 8px 10px !important;
  height: auto !important;
  
  &:hover {
    background-color: #1a4a8f !important;
    color: #ffffff !important;
  }
`;

export const EditProfileButton = styled(Button)`
  background-color: #2D6CCA !important;
  border: 0 !important;
  color: #ffffff !important;
  font-size: 16px !important;
  border-radius: 8px !important;
  padding: 6px 10px !important;
  height: auto !important;
  
  &:hover {
    background-color: #1a4a8f !important;
    border: 0 !important;
    color: #ffffff !important;
  }
  
  &:disabled {
    cursor: not-allowed !important;
  }
`;

export const CancelButton = styled(Button)`
  background-color: #ededed !important;
  border: 0 !important;
  color: #121212 !important;
  font-size: 16px !important;
  font-weight: 500 !important;
  border-radius: 8px !important;
  padding: 8px 16px !important;
  height: auto !important;

  &:hover {
    background-color: #d4d4d4 !important;
    border: 0 !important;
    color: #121212 !important;
  }
`;

export const DisableButton = styled(Button)`
  background-color: #f5222d !important;
  border: 0 !important;
  color: #ffffff !important;
  font-size: 16px !important;
  font-weight: 500 !important;
  border-radius: 8px !important;
  padding: 8px 16px !important;
  height: auto !important;

  &:hover {
    background-color: #d32f2f !important;
    border: 0 !important;
    color: #ffffff !important;
  }

  &:disabled {
    cursor: not-allowed !important;
  }
`;

export const ActionButtons = styled(Space)`
  margin-top: 16px;
`;

export const StyledTable = styled(Table)`
  .ant-table {
    background: ${(props) => getThemeValue(props, '#0d1117', '#ffffff')};
    color: ${(props) => getThemeValue(props, '#fff', '#1a1a1a')};
  }

  .ant-table-thead > tr > th {
    background: ${(props) => getThemeValue(props, '#161b22', '#fafafa')};
    color: ${(props) => getThemeValue(props, '#fff', '#1a1a1a')};
    border-bottom: 1px solid ${(props) => getThemeValue(props, '#30363d', '#e8e8e8')};
    font-weight: 600;
  }

  .ant-table-tbody > tr {
    &:hover {
      background: ${(props) => getThemeValue(props, '#161b22', '#f5f5f5')};
    }
  }

  .ant-table-tbody > tr > td {
    border-bottom: 1px solid ${(props) => getThemeValue(props, '#21262d', '#e8e8e8')};
    color: ${(props) => getThemeValue(props, '#fff', '#1a1a1a')};
  }
`;

export const StyledSwitch = styled(Switch)`
  &.ant-switch-checked {
    background-color: #1890ff;
  }
`;

export const StyledProgress = styled(Progress)`
  .ant-progress-text {
    color: ${(props) => getThemeValue(props, '#fff', '#1a1a1a')};
  }
`;

export const PreferenceRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: ${(props) => getThemeValue(props, '#0d1117', '#f9f9f9')};
  border-radius: 8px;
  border: 1px solid ${(props) => getThemeValue(props, '#21262d', '#e8e8e8')};
  margin-bottom: 12px;
`;

export const PreferenceInfo = styled.div`
  flex: 1;
`;

export const PreferenceLabel = styled(Typography.Text)`
  font-size: 14px;
  color: ${(props) => getThemeValue(props, '#fff', '#1a1a1a')} !important;
  font-weight: 600;
  display: block;
  margin-bottom: 4px;
`;

export const PreferenceDescription = styled.p`
  font-size: 12px;
  color: ${(props) => getThemeValue(props, '#8b949e', '#666')};
  line-height: normal;
`;

export const PreferenceEditRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const PreferenceEditInfo = styled.div`
  flex: 1;
`;

export const PreferenceSubText = styled(Typography.Text)`
  display: block;
  font-size: 12px;
`;

export const EmptyState = styled.div`
  text-align: center;
  padding: 40px;
  color: ${(props) => getThemeValue(props, '#8b949e', '#666')};
`;

export const GridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 24px;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

export const TwoStepMethodOption = styled(Radio)`
  display: flex !important;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;

  .ant-radio + span {
    display: inline-flex;
    align-items: center;
    gap: 8px;
  }
`;

export const TwoStepInputWrap = styled.div`
  max-width: 320px;
`;

export const TwoStepEnableBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 12px;
  background: ${(props) => getThemeValue(props, '#0d1117', '#f9f9f9')};
  border-radius: 8px;
  border: 1px solid ${(props) => getThemeValue(props, '#21262d', '#e8e8e8')};
`;

export const TwoStepSwitchWrap = styled.div`
  width: fit-content;
`;

export const TwoStepEnableTitle = styled.p`
  margin: 0;
  font-weight: 600;
  color: ${(props) => getThemeValue(props, '#fff', '#1a1a1a')};
`;

export const TwoStepEnableDescription = styled.p`
  margin: 0;
  font-size: 12px;
  color: ${(props) => getThemeValue(props, '#8b949e', '#666')};
  line-height: normal;
`;

export const DisableButtonWrap = styled.div`
  margin-top: 16px;
`;

export const ResendCodeButton = styled.button`
  background: none;
  border: none;
  color: #2d6cca;
  cursor: pointer;
  font-size: 14px;
  text-decoration: underline;
  padding: 0;
  margin-top: 8px;
  display: block;
  margin-left: auto;
`;

export const ModalWarningText = styled.p`
  color: #ff4d4f;
  font-weight: 500;
`;

export const EditingBadge = styled.div`
  position: absolute;
  top: -12px;
  left: 24px;
  background: #2D6CCA;
  color: white;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  z-index: 1;
`;

export const EditingCard = styled(Card)`
  border-radius: 12px;
  border: 1px solid #2D6CCA;
  background: ${(props) => getThemeValue(props, '#161b22', '#ffffff')};
  margin-bottom: 24px;
  box-shadow: ${(props) => getThemeValue(props, '0 2px 8px rgba(0, 0, 0, 0.3)', '0 2px 8px rgba(0, 0, 0, 0.08)')};
  position: relative;

  .ant-card-body {
    padding: 16px;
  }
`;

// New Tab Styles
export const StyledTabs = styled(Tabs)`
  margin-top: 24px;

  .ant-tabs-nav {
    background: ${(props) => getThemeValue(props, '#161b22', '#ffffff')};
    border-bottom: 1px solid ${(props) => getThemeValue(props, '#30363d', '#e8e8e8')};
    border-radius: 12px 12px 0 0;
    padding: 0 16px;
    margin-bottom: 0 !important;
  }

  .ant-tabs-tab {
    color: ${(props) => getThemeValue(props, '#8b949e', '#555')} !important;
    border-radius: 8px 8px 0 0 !important;
    margin-right: 8px;

    .ant-tabs-tab-btn {
      font-weight: 600;
    }

    &:hover {
      color: ${(props) => getThemeValue(props, '#c9d1d9', '#222')} !important;
    }
  }

  .ant-tabs-tab-active {
    .ant-tabs-tab-btn {
      color: #2D6CCA !important;
    }
  }

  .ant-tabs-ink-bar {
    background: #2D6CCA !important;
  }

  .ant-tabs-content {
    background: ${(props) => getThemeValue(props, '#161b22', '#ffffff')};
    border: 1px solid ${(props) => getThemeValue(props, '#30363d', '#e8e8e8')};
    border-top: none;
    border-radius: 0 0 12px 12px;
  }

  .ant-tabs-tabpane {
    padding: 24px;

    @media (max-width: 768px) {
      padding: 16px;
    }

    @media (max-width: 480px) {
      padding: 12px;
    }
  }

  @media (max-width: 768px) {
    .ant-tabs-nav {
      padding: 0 12px;
    }

    .ant-tabs-tab {
      margin-right: 4px;
      font-size: 14px;
    }
  }

  @media (max-width: 480px) {
    .ant-tabs-nav {
      padding: 0 8px;
    }

    .ant-tabs-tab {
      margin-right: 2px;
      font-size: 12px;
      padding: 8px 6px !important;
    }
  }
`;

// Error and Validation Styles
export const FormFieldWithError = styled.div`
  position: relative;
  margin-bottom: 16px;
`;

export const ErrorMessage = styled.div`
  color: #f5222d;
  font-size: 12px;
  margin-top: 6px;
  display: flex;
  align-items: center;
  gap: 6px;
  animation: slideDown 0.2s ease-out;

  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-4px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

export const SuccessMessage = styled.div`
  color: #52c41a;
  font-size: 12px;
  margin-top: 6px;
  display: flex;
  align-items: center;
  gap: 6px;
  animation: slideDown 0.2s ease-out;

  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-4px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

export const FieldHint = styled.p`
  font-size: 12px;
  color: ${(props) => getThemeValue(props, '#8b949e', '#999')};
  margin-top: 6px;
  line-height: 1.5;
  margin-bottom: 0;
`;

export const TabContent = styled.div`
  padding: 24px;
  background: ${(props) => getThemeValue(props, '#161b22', '#ffffff')};
  border-radius: 0 0 12px 12px;

  @media (max-width: 768px) {
    padding: 16px;
  }

  @media (max-width: 480px) {
    padding: 12px;
  }
`;

// Logo Upload Section Styles
export const LogoUploadContainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 24px;
  margin-bottom: 20px;
  padding: 16px;
  background: ${(props) => getThemeValue(props, '#0d1117', '#f9f9f9')};
  border-radius: 8px;
  border: 1px solid ${(props) => getThemeValue(props, '#21262d', '#e8e8e8')};

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    gap: 16px;
  }
`;

export const LogoPreviewBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

export const LogoActionsBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1;

  @media (max-width: 768px) {
    width: 100%;
    align-items: center;
  }
`;

export const UploadButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  width: 100%;
  flex-wrap: wrap;

  label {
    flex: 1;
    min-width: 200px;

    button {
      width: 100%;
    }
  }

  @media (max-width: 768px) {
    width: 100%;
    flex-direction: column;

    label {
      flex: 1;
      min-width: 0;

      button {
        width: 100%;
      }
    }
  }
`;

export const UploadHint = styled.p`
  font-size: 13px;
  color: ${(props) => getThemeValue(props, '#8b949e', '#999')};
  margin: 0;
  line-height: 1.5;

  @media (max-width: 768px) {
    text-align: center;
  }
`;

export const UploadSectionTitle = styled.h4`
  margin: 0 0 18px 0;
  font-size: 15px;
  font-weight: 600;
  color: ${(props) => getThemeValue(props, '#fff', '#1a1a1a')};
`;

export const DangerSection = styled.div`
  padding: 20px;
  background: ${(props) => getThemeValue(props, '#1a1a1a', '#fff7f6')};
  border: 2px solid #ff7875;
  border-radius: 8px;
`;

export const DangerSectionTitle = styled.h3`
  margin: 0 0 8px 0;
  font-size: 18px;
  font-weight: 600;
  color: #f5222d;
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const DangerSectionDescription = styled.p`
  margin: 0 0 20px 0;
  font-size: 14px;
  color: ${(props) => getThemeValue(props, '#8b949e', '#666')};
  line-height: 1.5;
`;

export const DangerBox = styled.div<{ $isDanger?: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 20px;
  padding: 18px;
  background: ${(props) => {
    if (props.$isDanger) {
      return getThemeValue(props, '#0a0a0a', '#fff8f8');
    }
    return getThemeValue(props, '#0a0a0a', '#fffaf9');
  }};
  border: 1px solid ${(props) => (props.$isDanger ? '#ff5252' : '#ffb3b3')};
  border-radius: 8px;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${(props) => (props.$isDanger ? '#ff4d4f' : '#ff9999')};
  }

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
`;

export const DangerBoxIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 8px;
  background: ${(props) => getThemeValue(props, 'rgba(255, 152, 0, 0.15)', 'rgba(255, 152, 0, 0.1)')};
  font-size: 22px;
  color: #ff9800;
  flex-shrink: 0;

  @media (max-width: 768px) {
    width: 40px;
    height: 40px;
    font-size: 18px;
  }
`;

export const DangerBoxContent = styled.div`
  flex: 1;
  min-width: 0;
`;

export const DangerBoxTitle = styled.h4`
  margin: 0 0 6px 0;
  font-size: 15px;
  font-weight: 600;
  color: ${(props) => getThemeValue(props, '#fff', '#1a1a1a')};
`;

export const DangerBoxDescription = styled.p`
  margin: 0;
  font-size: 13px;
  color: ${(props) => getThemeValue(props, '#8b949e', '#666')};
  line-height: 1.4;
`;

export const DangerButton = styled(Button)`
  background-color: #f5222d !important;
  border: 0 !important;
  color: #ffffff !important;
  font-size: 14px !important;
  font-weight: 500 !important;
  border-radius: 6px !important;
  padding: 6px 16px !important;
  height: auto !important;
  white-space: nowrap;

  &:hover {
    background-color: #d32f2f !important;
    color: #ffffff !important;
  }

  &:disabled {
    cursor: not-allowed !important;
    opacity: 0.6;
  }

  @media (max-width: 768px) {
    width: 100%;
  }
`;

export const DeactivateButton = styled(Button)`
  background-color: #faad14 !important;
  border: 0 !important;
  color: #000000 !important;
  font-size: 14px !important;
  font-weight: 500 !important;
  border-radius: 6px !important;
  padding: 6px 16px !important;
  height: auto !important;
  white-space: nowrap;

  &:hover {
    background-color: #ff9800 !important;
    color: #000000 !important;
  }

  &:disabled {
    cursor: not-allowed !important;
    opacity: 0.6;
  }

  @media (max-width: 768px) {
    width: 100%;
  }
`;

export const ConfirmationInput = styled(Input)`
  margin-top: 8px !important;
  padding: 8px 12px !important;
  border-radius: 6px !important;
  border: 1px solid ${(props) => getThemeValue(props, '#30363d', '#d9d9d9')} !important;
  background: ${(props) => getThemeValue(props, '#0d1117', '#ffffff')} !important;
  color: ${(props) => getThemeValue(props, '#c9d1d9', '#000000')} !important;

  &:focus {
    border-color: #f5222d !important;
    box-shadow: 0 0 0 2px rgba(245, 34, 45, 0.2) !important;
  }
`;

export const WarningBox = styled.div<{ $type?: 'warning' | 'danger' }>`
  padding: 14px;
  border-radius: 8px;
  border-left: 4px solid ${(props) => (props.$type === 'danger' ? '#ff5252' : '#ff9800')};
  background: ${(props) => {
    if (props.$type === 'danger') {
      return getThemeValue(props, 'rgba(255, 82, 82, 0.1)', '#fff5f5');
    }
    return getThemeValue(props, 'rgba(255, 152, 0, 0.1)', '#fffaf0');
  }};
`;

export const WarningTextSmall = styled.p`
  margin: 0;
  font-size: 13px;
  color: ${(props) => getThemeValue(props, '#e8e8e8', '#555')};
  line-height: 1.6;

  strong {
    color: ${(props) => getThemeValue(props, '#fff', '#000')};
    font-weight: 600;
  }
`;

export const DangerActionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const ModalTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

export const ModalIcon = styled.span<{ $color: string }>`
  font-size: 18px;
  color: ${(props) => props.$color};
  display: flex;
  align-items: center;
`;

export const ModalDeleteList = styled.ul`
  margin-left: 20px;
  font-size: 13px;
  color: #666;
  line-height: 1.8;
  margin-bottom: 0;
`;

export const ModalConfirmWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const ModalConfirmLabel = styled.p`
  font-size: 14px;
  font-weight: 500;
  margin: 0;
  color: ${(props) => getThemeValue(props, '#c9d1d9', '#1a1a1a')};
`;

export const TaxProgressWrapper = styled.div`
  margin-top: 16px;
`;

export const SectionHintText = styled.span`
  font-size: 12px;
  display: block;
  color: ${(props) => getThemeValue(props, '#8b8b8b', '#00000073')};
`;

export const ProfileActionGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const ProfileUploadSection = styled.div`
  margin-top: 24px;
  border-top: 1px solid ${(props) => getThemeValue(props, '#30363d', '#e8e8e8')};
  padding-top: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const ProfileLogoAvatar = styled(Avatar)`
  border: 2px solid #2d6cca !important;
`;

export const UploadErrorText = styled.div`
  color: #f56565;
  font-size: 14px;
  margin-top: 8px;
  font-weight: 500;
`;

export const DomainCardWrapper = styled.div`
  padding: 14px 16px;
  border-radius: 8px;
  border: 1px solid ${(props) => getThemeValue(props, '#30363d', '#e8e8e8')};
  background: ${(props) => getThemeValue(props, '#0d1117', '#fafafa')};
  margin-bottom: 12px;
`;

export const DomainCardTopRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: space-between;
`;

export const DomainCardMetaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

export const DomainName = styled.span`
  font-weight: 600;
  font-size: 14px;
  color: ${(props) => getThemeValue(props, '#c9d1d9', '#1a1a1a')};
`;

export const DnsInfoRow = styled.div`
  display: flex;
  gap: 24px;
  margin-bottom: 4px;
  flex-wrap: wrap;
`;

export const DnsPropagationHint = styled.span`
  font-size: 12px;
  color: ${(props) => getThemeValue(props, '#8b949e', '#888')};
  margin-top: 6px;
  display: block;
`;

export const DnsSetupCard = styled.div`
  margin-top: 10px;
  padding: 14px 16px;
  border-radius: 10px;
  border: 1px solid ${(props) => getThemeValue(props, 'rgba(45, 108, 202, 0.35)', '#d6e4ff')};
  background: ${(props) => getThemeValue(props, 'rgba(45, 108, 202, 0.08)', '#f5f9ff')};
`;

export const DnsSetupHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  font-size: 13px;
  color: ${(props) => getThemeValue(props, '#f0f0f0', '#1a1a1a')};
  margin-bottom: 12px;
`;

export const DnsSetupIcon = styled(InfoCircleOutlined)`
  color: #2d6cca;
  font-size: 14px;
`;

export const DnsStepList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

export const DnsStep = styled.div`
  display: flex;
  gap: 10px;
`;

export const DnsStepBadge = styled.span`
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 600;
  color: #fff;
  background: #2d6cca;
`;

export const DnsStepBody = styled.div`
  flex: 1;
  min-width: 0;
`;

export const DnsStepTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  margin-bottom: 6px;
  color: ${(props) => getThemeValue(props, '#f0f0f0', '#1a1a1a')};
`;

export const DnsFieldRow = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 6px;
  flex-wrap: wrap;
`;

export const DnsFieldPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 20px;
  font-size: 12px;
  background: ${(props) => getThemeValue(props, '#21262d', '#eef2f7')};
  color: ${(props) => getThemeValue(props, '#c9d1d9', '#444')};

  strong {
    color: ${(props) => getThemeValue(props, '#8b949e', '#888')};
    font-weight: 600;
  }
`;

export const CodeBlock = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  background: ${(props) => getThemeValue(props, '#0d1117', '#f5f5f5')};
  border-radius: 6px;
  font-family: monospace;
  font-size: 13px;
  margin-top: 6px;
`;

export const AddDomainFormWrapper = styled.div`
  padding: 16px;
  border-radius: 8px;
  border: 1px solid #2d6cca;
  background: ${(props) => getThemeValue(props, '#161b22', '#f0f5ff')};
  margin-top: 16px;
`;

export const DomainInfoCard = styled.div`
  padding: 14px 16px;
  border-radius: 10px;
  border: 1px solid ${(props) => getThemeValue(props, '#30363d', '#e8e8e8')};
  background: ${(props) => getThemeValue(props, '#0d1117', '#fafafa')};
`;

export const ModalSectionTitle = styled.h4`
  margin: 0 0 4px;
  font-size: 14px;
  font-weight: 600;
  color: ${(props) => getThemeValue(props, '#f0f0f0', '#1a1a1a')};
`;

export const ModalSectionDesc = styled.p`
  margin: 0 0 16px;
  font-size: 13px;
  line-height: 1.5;
  color: ${(props) => getThemeValue(props, '#8b949e', '#666666')};
`;

export const DomainTypeOptionsRow = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
`;

export const DomainTypeOption = styled.div<{ $selected?: boolean }>`
  flex: 1 1 200px;
  min-width: 180px;
  padding: 12px 14px;
  border-radius: 8px;
  cursor: pointer;
  border: 1px solid ${(props) =>
    props.$selected ? '#2d6cca' : getThemeValue(props, '#434343', '#d9d9d9')};
  background: ${(props) =>
    props.$selected
      ? getThemeValue(props, 'rgba(45, 108, 202, 0.15)', '#eaf1ff')
      : getThemeValue(props, '#0d1117', '#ffffff')};
  transition: border-color 0.2s, background 0.2s;

  &:hover {
    border-color: #2d6cca;
  }
`;

export const DomainTypeOptionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const DomainTypeRadioDot = styled.span<{ $selected?: boolean }>`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 2px solid ${(props) =>
    props.$selected ? '#2d6cca' : getThemeValue(props, '#4a5568', '#bfbfbf')};

  &::after {
    content: '';
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #2d6cca;
    display: ${(props) => (props.$selected ? 'block' : 'none')};
  }
`;

export const DomainTypeOptionTitle = styled.span`
  font-weight: 600;
  font-size: 14px;
  color: ${(props) => getThemeValue(props, '#f0f0f0', '#1a1a1a')};
`;

export const DomainTypeOptionExample = styled.div`
  font-size: 12px;
  color: ${(props) => getThemeValue(props, '#8b949e', '#666666')};
  margin-top: 4px;
  margin-left: 24px;
  word-break: break-word;
  line-height: 1.4;
`;

export const DomainSkeletonList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const EmptyDescription = styled.span`
  color: ${(props) => getThemeValue(props, '#8b949e', '#aaa')};
`;

export const PlatformTagStyled = styled(Tag)<{ $bgColor: string }>`
  background: ${(props) => props.$bgColor} !important;
  color: #fff !important;
  border: none !important;
  border-radius: 4px !important;
`;

export const DomainGlobalIcon = styled(GlobalOutlined)`
  color: #1890ff;
`;

export const DomainCardAlert = styled(Alert)`
  margin-top: 10px !important;
  border-radius: 6px !important;
`;

export const DomainFormAlert = styled(Alert)`
  border-radius: 8px !important;
`;

export const CopyCodeIcon = styled(CopyOutlined)`
  cursor: pointer;
  color: #1890ff;
  flex-shrink: 0;
`;

export const CopiedCheckIcon = styled(CheckOutlined)`
  color: #52c41a;
  cursor: pointer;
  flex-shrink: 0;
`;

